import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { RecommendationContext } from 'src/components/restructure/product/contexts/Recommendations/recommendations-context'
import { POSITION_MAP } from 'src/components/restructure/product/shelves/GenericShelfV2/GenericShelfV2'
import type { DataRecommendation } from 'src/components/restructure/product/types/linx-recommendations'
import type { Product } from 'src/components/restructure/product/types/product'

import type {
  DYPositionContent,
  ResolvedTab,
  VitrineLargeProps,
  VitrineLargeTab,
} from '../types'
import { fetchCollectionProductsByTab } from '../utils/fetchCollectionProducts'

const SECTION_NAME = '[Campanhas 2.0] Vitrine Large'

const normalizePosition = (position?: string) => {
  if (!position) {
    return undefined
  }

  const normalizedValue = position.trim().toLowerCase()
  const normalizedSpaces = normalizedValue.replace(/\s+/g, ' ')
  const normalizedHyphen = normalizedSpaces.replace(/\s+/g, '-')

  const positionMap = POSITION_MAP as Record<string, string>

  return positionMap[normalizedSpaces] ?? positionMap[normalizedHyphen]
}

const getTabKey = (tab: VitrineLargeTab, index: number) =>
  `${tab.engine}:${tab.id ?? 'no-id'}:${index}`

const getTabLabel = (tab: VitrineLargeTab, index: number) => {
  const tabName = tab.tabName?.trim()

  return tabName && tabName.length > 0 ? tabName : `Tab ${index + 1}`
}

type UseVitrineLargeTabsParams = {
  theme: VitrineLargeProps['theme']
  position?: VitrineLargeProps['position']
  items?: VitrineLargeProps['items']
}

export function useVitrineLargeTabs({
  theme = 'light',
  position,
  items = [],
}: UseVitrineLargeTabsParams) {
  const [activeTabIndex, setActiveTabIndex] = useState(0)

  const [collectionProducts, setCollectionProducts] = useState<
    Record<string, Product[]>
  >({})

  const [collectionLoading, setCollectionLoading] = useState<
    Record<string, boolean>
  >({})

  const { dyDataRecommendations, loading: dyLoading } = useContext(
    RecommendationContext
  )

  const getDYContentByPosition = useCallback(
    (targetPosition?: string): DYPositionContent | undefined => {
      const normalizedTargetPosition = normalizePosition(targetPosition)

      if (!normalizedTargetPosition || !Array.isArray(dyDataRecommendations)) {
        return undefined
      }

      const recommendation = dyDataRecommendations.find(
        (item: DataRecommendation) =>
          normalizePosition(item?.position) === normalizedTargetPosition
      )

      const showcaseWithProducts = recommendation?.showcases?.find(
        (showcase: DataRecommendation['showcases'][number]) => {
          const recommendations = showcase?.displays?.[0]?.recommendations

          return Array.isArray(recommendations) && recommendations.length > 0
        }
      )

      const recommendations =
        showcaseWithProducts?.displays?.[0]?.recommendations

      if (!Array.isArray(recommendations) || recommendations.length === 0) {
        return undefined
      }

      return {
        products: recommendations,
        title: showcaseWithProducts?.title,
      }
    },
    [dyDataRecommendations]
  )

  const resolvedTabs = useMemo(() => {
    const seenTabKeys = new Set<string>()
    const visibleTabs: ResolvedTab[] = []

    items.forEach((tab, index) => {
      if (tab.engine !== 'DY') {
        const dedupeKey = `Coleção:${tab.id ?? ''}`

        if (seenTabKeys.has(dedupeKey)) {
          return
        }

        seenTabKeys.add(dedupeKey)
        visibleTabs.push({
          tab,
          tabKey: getTabKey(tab, index),
          tabLabel: getTabLabel(tab, index),
        })

        return
      }

      const normalizedPosition = normalizePosition(tab.position ?? position)
      const dyContent = getDYContentByPosition(normalizedPosition)

      if (!dyLoading && (!dyContent || dyContent.products.length === 0)) {
        return
      }

      const dedupeKey = `DY:${normalizedPosition ?? ''}`

      if (seenTabKeys.has(dedupeKey)) {
        return
      }

      seenTabKeys.add(dedupeKey)

      const dyTitle = dyContent?.title?.trim()

      visibleTabs.push({
        tab,
        tabKey: getTabKey(tab, index),
        tabLabel:
          dyTitle && dyTitle.length > 0 ? dyTitle : getTabLabel(tab, index),
        dyContent,
      })
    })

    return visibleTabs
  }, [items, position, getDYContentByPosition, dyLoading])

  const activeTabEntry = resolvedTabs[activeTabIndex]
  const activeTab = activeTabEntry?.tab
  const activeTabKey = activeTabEntry?.tabKey ?? ''

  useEffect(() => {
    if (!resolvedTabs.length) {
      return
    }

    if (!resolvedTabs[activeTabIndex]) {
      setActiveTabIndex(0)
    }
  }, [activeTabIndex, resolvedTabs])

  useEffect(() => {
    if (!activeTab || activeTab.engine !== 'Coleção' || !activeTab.id) {
      return
    }

    fetchCollectionProductsByTab({
      tab: {
        key: activeTabKey,
        idColecao: activeTab.id,
      },
      collectionProducts,
      setCollectionProducts,
      setCollectionLoading,
      theme,
      sectionName: SECTION_NAME,
    })
  }, [activeTab, activeTabKey, collectionProducts, theme, activeTab?.id])

  const activeProducts =
    !activeTab || activeTab.engine === 'DY'
      ? activeTabEntry?.dyContent?.products ?? []
      : collectionProducts[activeTabKey] ?? []

  const hasDynamicTabs = resolvedTabs.some((entry) => entry.tab.engine === 'DY')

  const allCollectionTabsLoaded = resolvedTabs
    .filter((entry) => entry.tab.engine === 'Coleção' && Boolean(entry.tab.id))
    .every((entry) =>
      Object.prototype.hasOwnProperty.call(collectionProducts, entry.tabKey)
    )

  const allTabsWithoutProducts = !resolvedTabs.length
    ? true
    : resolvedTabs.every((entry) => {
        if (entry.tab.engine === 'DY') {
          return (entry.dyContent?.products ?? []).length === 0
        }

        return (collectionProducts[entry.tabKey] ?? []).length === 0
      })

  const canHideComponentByEmptyState =
    allCollectionTabsLoaded && (!hasDynamicTabs || !dyLoading)

  const activeTabLoading =
    Boolean(activeTab?.engine === 'Coleção') &&
    Boolean(activeTab && collectionLoading[activeTabKey])

  const tabsItems = resolvedTabs.map(({ tabKey, tabLabel }) => ({
    key: tabKey,
    label: tabLabel,
  }))

  return {
    activeTab,
    activeTabIndex,
    activeTabKey,
    activeTabLoading,
    activeProducts,
    allTabsWithoutProducts,
    canHideComponentByEmptyState,
    dyLoading,
    resolvedTabs,
    setActiveTabIndex,
    tabsItems,
  }
}
